import { toast } from 'sonner';

import { addNotification, getNotificationById } from '@/stores/useNotificationStore';
import type { NotificationInput, NotificationItem, NotificationType } from '@/types/notification.types';

import Toast from './Toast';

interface ToastOptions {
  duration?: number;
  isNotification?: boolean;
  /** When false, persist to Notification Center only (no floating toast). Default true. */
  showPopup?: boolean;
  showProgressBar?: boolean;
}

export type ToastInput = NotificationInput & ToastOptions;

type ToastInputShort = Omit<ToastInput, 'type' | 'title'> & { type?: NotificationType };

/**
 * Show a toast notification and optionally add it to the Notification store.
 * Returns the notification ID.
 */
export const showToast = ({
  isNotification,
  showPopup = true,
  showProgressBar = true,
  duration = 5000,
  ...notificationInput
}: ToastInput): void => {
  // Add to notification store (returns the generated id)
  const notificationId = isNotification && addNotification(notificationInput);

  if (!showPopup) {
    return;
  }

  const id = notificationId || notificationInput.id;

  if (!isNotification || !notificationId) {
    // Not intended as notification or Store not initialized, show toast without persistence:
    toast.custom(
      (toastId) => (
        <Toast
          id={id || toastId}
          {...notificationInput}
          isViewed={false}
          showProgressBar={showProgressBar}
          createdAt={Date.now()}
          duration={duration}
        />
      ),
      {
        ...(id && { id }),
        duration,
      },
    );

    return;
  }

  // Get the notification from store to pass to Toast
  const notification = getNotificationById(id as string);

  // Show toast with store-managed notification
  toast.custom(
    () => (
      <Toast
        {...(notification as NotificationItem)}
        isNotification={true}
        createdAt={Date.now()} // override createdAt with current time to reset progress bar when toast is shown again with same id
        showProgressBar={showProgressBar}
        duration={duration}
      />
    ),
    {
      id,
      duration,
    },
  );
};

/**
 * Examples:
 *
 * - Simple toast without being added to Notification Center:
 * ```
 *    customToast('Toast with default info icon and without notification');
 *    customToast('Toast with error icon w/o notification', { type: 'error' });
 *    customToast('Toast with a success icon w/o notification', { type: 'success', duration: 10000, actionButtons: [ ... ] });
 * ```
 *
 * - Toast with Notifications Center:
 * ```
 *    customToast.success('A success notification');
 *    customToast.success('A success notification with data', { id: 'abc',  metadata: { txid: '1234' } });
 *    customToast.error('Some error with notification');
 *    customToast.warning('Some warning with notification');
 *    customToast.progress('Pending action with notification');
 *    customToast.quanta('Pending action with notification');
 *    customToast.info('Info with notification');
 * ```
 *
 * - Notification Center only (no floating popup):
 * ```
 *    customToast.error('Swap failed', { description: '...', showPopup: false });
 * ```
 */
export const customToast = (title: string, props?: ToastInputShort): void => {
  showToast({ type: 'info', title, ...props });
};

customToast.success = (title: string, props?: ToastInputShort): void => {
  showToast({ type: 'success', title, isNotification: true, ...props });
};

customToast.error = (title: string, props?: ToastInputShort): void => {
  showToast({ type: 'error', title, isNotification: true, ...props });
};

customToast.warning = (title: string, props?: ToastInputShort): void => {
  showToast({ type: 'warning', title, isNotification: true, ...props });
};

customToast.info = (title: string, props?: ToastInputShort): void => {
  showToast({ type: 'info', title, isNotification: true, ...props });
};

customToast.progress = (title: string, props?: ToastInputShort): void => {
  showToast({ type: 'progress', title, isNotification: true, ...props });
};

customToast.quanta = (title: string, props?: ToastInputShort): void => {
  showToast({ type: 'quanta', title, isNotification: true, ...props });
};
